import { ReactNode, useMemo } from 'react'
import { styled } from '@mui/material/styles'
import { Button, IconButton, Typography } from '@mui/material'
import { usePanelHistoryTop } from 'weblite-router'
import {
  AcademyIllustrationIcon,
  CheckCircleIcon,
  CheckIcon,
  ClearIcon,
  WDiamondIcon,
  WOnlineClassIcon,
  WParkTreeListIcon,
} from '@weblite/icons'
import {
  closePremiumFeatures,
  openPremiumFeaturesRequestForm,
} from '$logic/router'
import { t } from '$i18n'
import { theme } from '$setup/material-theme'
import { getLastPremiumFeaturesRequestTime } from '$logic/payment'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { DAY_IN_MILLISECONDS } from '$func/date'

const PREMIUM_FEATURES = [
  {
    icon: <WOnlineClassIcon sx={{ width: '40px', height: '40px' }} />,
    title: t('premiumFeatures.onlineClass.title'),
    subtitle: [
      t('premiumFeatures.onlineClass.sub1'),
      t('premiumFeatures.onlineClass.sub2'),
    ],
  },
  {
    icon: <AcademyIllustrationIcon sx={{ width: '40px', height: '40px' }} />,
    title: t('premiumFeatures.workspace.title'),
    subtitle: [
      t('premiumFeatures.workspace.sub1'),
      t('premiumFeatures.workspace.sub2'),
    ],
  },
  {
    icon: <WParkTreeListIcon sx={{ width: '40px', height: '40px' }} />,
    title: t('premiumFeatures.courseVitrine.title'),
    subtitle: [
      t('premiumFeatures.courseVitrine.sub1'),
      t('premiumFeatures.courseVitrine.sub2'),
    ],
  },
] as {
  icon: ReactNode
  title: string
  subtitle: string[]
}[]

export function PremiumFeaturesIntroduction() {
  const panelTop = usePanelHistoryTop('profile')
  const [topPage] = panelTop?.location?.split('/').slice(-1) ?? []
  const isTopPage = topPage === 'PREMIUM_FEATURES_INTRODUCTION'

  const requestIsSent = useMemo(() => {
    return (
      dateWithOffset() - getLastPremiumFeaturesRequestTime() <
      DAY_IN_MILLISECONDS
    )
  }, [isTopPage])

  return (
    <Root>
      <CloseIconWrapper onClick={closePremiumFeatures}>
        <ClearIcon sx={{ color: 'neutral000' }} />
      </CloseIconWrapper>

      <StyledTypography
        marginBottom="32px"
        alignItems="center"
        variant="h2"
        color="neutral1000"
      >
        <WDiamondIcon
          sx={{ width: '40px', height: '40px', color: 'secondary500' }}
        />
        {t('premiumFeatures.weblite')}
      </StyledTypography>

      {PREMIUM_FEATURES.map(feature => (
        // eslint-disable-next-line react/jsx-key
        <Wrapper>
          {feature.icon}
          <Typography>{feature.title}</Typography>
          {feature.subtitle.map(text => (
            // eslint-disable-next-line react/jsx-key

            <Typography
              variant="body2"
              display="flex"
              gap="8px"
              color="neutral700"
            >
              <CheckIcon
                sx={{ width: '20px', height: '20px', color: 'secondary500' }}
              />
              {text}
            </Typography>
          ))}
        </Wrapper>
      ))}

      <CallRequestButton requestIsSent={requestIsSent} />
    </Root>
  )
}

const Root = styled('div')`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${({ theme }) => theme.palette.neutral200} !important;
  overflow: auto;
`

const StyledTypography = styled(Typography)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: -18px;
`

const Wrapper = styled('div')`
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.1);
  :last-of-type {
    margin-bottom: 16px;
  }
`

const CloseIconWrapper = styled(IconButton)`
  margin-left: auto;
  background-color: ${({ theme }) => theme.palette.neutral500};
  border-radius: 50%;
`

function CallRequestButton({ requestIsSent }: { requestIsSent: boolean }) {
  return (
    <StyledRequestButton
      disabled={requestIsSent}
      data-submitted={requestIsSent}
      onClick={() => {
        analite('OPEN_PREMIUM_FEATURES_REQUEST_FORM')
        openPremiumFeaturesRequestForm()
      }}
    >
      {requestIsSent && (
        <CheckCircleIcon
          sx={{ color: 'neutral000', height: '20px', width: '20px' }}
        />
      )}
      <Typography variant="subtitle1" color="neutral000">
        {requestIsSent
          ? t('premiumFeatures.callRequestSent')
          : t('premiumFeatures.submit')}
      </Typography>
    </StyledRequestButton>
  )
}

const StyledRequestButton = styled(Button)(() => ({
  gap: '8px',
  marginTop: 'auto',
  minHeight: '48px',

  backgroundColor: theme.palette.primary500,
  "&[data-submitted='true']": {
    backgroundColor: theme.palette.success700,
  },
  '&:hover': {
    backgroundColor: theme.palette.primary500,
    "&[data-submitted='true']": {
      backgroundColor: theme.palette.success700,
    },
  },
}))
